/**
 * ExpressionAutocomplete - Autocomplete dropdown for expression intellisense
 *
 * Two-panel design:
 * - Left panel: List of methods grouped by category
 * - Right panel: Detailed documentation for selected method
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  mergeClasses,
  Divider,
  Portal,
} from '@coreai/fluentui-react';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import {
  TextTFilled,
  NumberSymbolFilled,
  BracesVariableFilled,
  CalendarRegular,
  CodeFilled,
  ArrowRightFilled,
} from '@fluentui/react-icons';
import type { ExtensionMethodMeta } from '@/utils/expressions';

const useStyles = makeStyles({
  container: {
    position: 'fixed', // Use fixed positioning since we render via portal
    // Fluent v9 Dialog overlays render at very high z-index tiers
    // (~1_000_000). The autocomplete must appear above the modal when
    // the user types `${` inside `ExpandableEditorDialog`, so we sit
    // above Fluent's overlay tier. Outside a dialog this is harmless.
    zIndex: 10_000_000,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow28,
    display: 'flex',
    flexDirection: 'column',
    width: '600px',
    maxWidth: 'calc(100vw - 40px)',
    height: '420px',
    maxHeight: 'calc(100vh - 200px)',
    overflow: 'hidden',
    '@media (forced-colors: active)': {
      border: '1px solid CanvasText',
      backgroundColor: 'Canvas',
    },
  },
  // Two-pane body. Sits above the keyboard-hint footer so the panes
  // share scroll boundaries and the footer never moves.
  panes: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    minHeight: 0,
  },

  // Left panel - method list
  listPanel: {
    width: '240px',
    minWidth: '200px',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    ...thinScrollbarStyles,
  },

  // Right panel - documentation
  docPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: tokens.colorNeutralBackground2,
    ...thinScrollbarStyles,
  },

  // Category header
  categoryHeader: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },

  // Method item in list
  methodItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    transitionProperty: 'background-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '&:active': {
      transform: 'scale(0.99)',
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      '&:active': {
        transform: 'none',
      },
    },
  },
  methodItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  methodIcon: {
    flexShrink: 0,
    width: '16px',
    height: '16px',
  },
  methodName: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  methodSignature: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
  },

  // Documentation panel content
  docContent: {
    padding: tokens.spacingHorizontalL,
  },
  docHeader: {
    marginBottom: tokens.spacingVerticalM,
  },
  docTitle: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  docSignature: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXS,
  },
  docReturnType: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  docReturnTypeBadge: {
    backgroundColor: tokens.colorNeutralBackground4,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },

  // Section in documentation
  docSection: {
    marginTop: tokens.spacingVerticalL,
  },
  docSectionTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalS,
  },
  docDescription: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
  },

  // Parameters table
  paramsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase200,
  },
  paramsRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  paramsCell: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    verticalAlign: 'top',
    '&:first-child': {
      paddingLeft: 0,
    },
  },
  paramName: {
    fontFamily: tokens.fontFamilyMonospace,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  paramType: {
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase100,
  },
  paramOptional: {
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase100,
  },

  // Example code block
  exampleBlock: {
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },

  // No results
  noResults: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
  },

  // Empty doc panel
  emptyDoc: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
  },

  // Icon colors
  iconString: { color: tokens.colorPaletteGreenForeground1 },
  iconNumber: { color: tokens.colorPaletteBlueForeground2 },
  iconBoolean: { color: tokens.colorPaletteMarigoldForeground1 },
  iconArray: { color: tokens.colorPalettePurpleForeground2 },
  iconObject: { color: tokens.colorPaletteTealForeground2 },
  iconDate: { color: tokens.colorPalettePinkForeground2 },
  iconAny: { color: tokens.colorNeutralForeground3 },

  // Footer with keyboard hints — mirrors the pattern in the rail's
  // function picker so the two surfaces feel like cousins.
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  hint: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
  },
  kbd: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusSmall,
    '@media (forced-colors: active)': {
      border: '1px solid CanvasText',
      color: 'CanvasText',
    },
  },
});

/**
 * Autocomplete suggestion item
 */
export interface AutocompleteSuggestion {
  /** Method/property name */
  label: string;
  /** Type of suggestion */
  type: 'method' | 'property' | 'variable' | 'function';
  /** Return type or brief description */
  detail: string;
  /** Full description */
  documentation?: string;
  /** Text to insert when selected */
  insertText: string;
  /** Example usage */
  example?: string;
  /** Arguments for methods */
  args?: Array<{ name: string; type: string; optional?: boolean; description?: string }>;
  /** Category for grouping (e.g., 'Case', 'Search', 'Convert') */
  category?: string;
  /** Return type */
  returnType?: string;
}

export interface ExpressionAutocompleteProps {
  /** Position for the dropdown */
  position: { top: number; left: number };
  /** Available suggestions */
  suggestions: AutocompleteSuggestion[];
  /** Called when a suggestion is selected */
  onSelect: (suggestion: AutocompleteSuggestion) => void;
  /** Called when the dropdown should close */
  onClose: () => void;
  /** Currently selected index */
  selectedIndex: number;
  /** Called when selected index changes */
  onSelectedIndexChange: (index: number) => void;
  /** Filter text to highlight matching parts */
  filterText?: string;
  /** Header text (e.g., "String Methods") */
  headerText?: string;
  /** Called when mouse down on dropdown (to prevent blur from closing it) */
  onMouseDown?: () => void;
}

/**
 * Get icon for a return type
 */
function getTypeIcon(returnType: string, styles: ReturnType<typeof useStyles>): JSX.Element {
  const type = returnType?.toLowerCase() || 'any';
  switch (type) {
    case 'string':
      return <TextTFilled className={mergeClasses(styles.methodIcon, styles.iconString)} />;
    case 'number':
      return <NumberSymbolFilled className={mergeClasses(styles.methodIcon, styles.iconNumber)} />;
    case 'boolean':
      return <CodeFilled className={mergeClasses(styles.methodIcon, styles.iconBoolean)} />;
    case 'array':
      return <BracesVariableFilled className={mergeClasses(styles.methodIcon, styles.iconArray)} />;
    case 'object':
      return <BracesVariableFilled className={mergeClasses(styles.methodIcon, styles.iconObject)} />;
    case 'date':
    case 'datetime':
      return <CalendarRegular className={mergeClasses(styles.methodIcon, styles.iconDate)} />;
    default:
      return <CodeFilled className={mergeClasses(styles.methodIcon, styles.iconAny)} />;
  }
}

/**
 * Format method signature for display
 */
function formatSignature(suggestion: AutocompleteSuggestion): string {
  if (suggestion.type === 'property') {
    return '';
  }
  if (!suggestion.args || suggestion.args.length === 0) {
    return '()';
  }
  const argStrings = suggestion.args.map((arg) => {
    const optionalMark = arg.optional ? '?' : '';
    return `${arg.name}${optionalMark}`;
  });
  return `(${argStrings.join(', ')})`;
}

/**
 * Category display order and labels
 */
const CATEGORY_ORDER: Record<string, number> = {
  'Suggested': 0,
  'Workflow': 1,
  'Variables': 2,
  'Functions': 3,
  'Case': 4,
  'Whitespace': 5,
  'Search': 6,
  'Extract': 7,
  'Replace': 8,
  'Padding': 9,
  'Transform': 10,
  'Filter': 11,
  'Test': 12,
  'Combine': 13,
  'Access': 14,
  'Aggregate': 15,
  'Convert': 16,
  'Create': 17,
  'Merge': 18,
  'Immutable': 19,
  'Format': 20,
  'Get': 21,
  // DateTime & Duration categories (from Luxon helpers)
  'DateTime Arithmetic': 30,
  'DateTime Boundaries': 31,
  'DateTime Comparison': 32,
  'DateTime Timezone': 33,
  'DateTime Format': 34,
  'DateTime Properties': 35,
  'Duration': 36,
  'Edit': 40,
  'Validate': 41,
  'Encode': 42,
  'Math': 43,
  'Compare': 44,
  'Other': 99,
};

function getCategoryOrder(category: string): number {
  return CATEGORY_ORDER[category] ?? 50;
}

/**
 * ExpressionAutocomplete component
 */
export const ExpressionAutocomplete = ({
  position,
  suggestions,
  onSelect,
  onClose,
  selectedIndex,
  onSelectedIndexChange,
  filterText = '',
  headerText,
  onMouseDown,
}: ExpressionAutocompleteProps) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);
  const [visibleSuggestions, setVisibleSuggestions] = useState(suggestions);

  // Filter suggestions based on filter text
  useEffect(() => {
    if (!filterText) {
      setVisibleSuggestions(suggestions);
      return;
    }

    const filtered = suggestions.filter((s) =>
      s.label.toLowerCase().includes(filterText.toLowerCase())
    );
    setVisibleSuggestions(filtered);

    // Reset selection if current selection is out of bounds
    if (selectedIndex >= filtered.length) {
      onSelectedIndexChange(Math.max(0, filtered.length - 1));
    }
  }, [suggestions, filterText, selectedIndex, onSelectedIndexChange]);

  // Group suggestions by category
  const groupedSuggestions = useMemo(() => {
    const groups: Record<string, AutocompleteSuggestion[]> = {};

    for (const suggestion of visibleSuggestions) {
      const category = suggestion.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(suggestion);
    }

    // Sort categories by order
    const sortedCategories = Object.keys(groups).sort(
      (a, b) => getCategoryOrder(a) - getCategoryOrder(b)
    );

    return { groups, sortedCategories };
  }, [visibleSuggestions]);

  // Build a flat index mapping for keyboard navigation
  const flatSuggestions = useMemo(() => {
    const flat: AutocompleteSuggestion[] = [];
    for (const category of groupedSuggestions.sortedCategories) {
      flat.push(...(groupedSuggestions.groups[category] || []));
    }
    return flat;
  }, [groupedSuggestions]);

  // Get currently selected suggestion
  const selectedSuggestion = flatSuggestions[selectedIndex];

  // Scroll selected item into view. Honour `prefers-reduced-motion` —
  // smooth scroll fights motion-sensitive users when arrowing through a
  // long list.
  useEffect(() => {
    if (!selectedRef.current) return;
    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    selectedRef.current.scrollIntoView({
      block: 'nearest',
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, [selectedIndex]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          onSelectedIndexChange(
            selectedIndex < flatSuggestions.length - 1 ? selectedIndex + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          onSelectedIndexChange(
            selectedIndex > 0 ? selectedIndex - 1 : flatSuggestions.length - 1
          );
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          e.stopPropagation();
          if (flatSuggestions[selectedIndex]) {
            onSelect(flatSuggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
      }
    },
    [selectedIndex, flatSuggestions, onSelect, onClose, onSelectedIndexChange]
  );

  // Attach keyboard listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyDown]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle item click
  const handleItemClick = useCallback(
    (suggestion: AutocompleteSuggestion, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(suggestion);
    },
    [onSelect]
  );

  // Calculate flat index for a suggestion
  const getFlatIndex = useCallback(
    (suggestion: AutocompleteSuggestion): number => {
      return flatSuggestions.indexOf(suggestion);
    },
    [flatSuggestions]
  );

  // Stable `id` prefix so `aria-activedescendant` on the listbox can
  // point at whichever row is currently selected. Matters for screen
  // readers — they announce the active descendant when arrow keys move
  // selection without focus.
  const listboxId = 'expression-autocomplete-list';
  const optionId = (suggestion: AutocompleteSuggestion): string =>
    `${listboxId}-${(suggestion.category ?? 'Other')
      .replace(/\s+/g, '-')
      .toLowerCase()}-${suggestion.label}`;

  if (visibleSuggestions.length === 0) {
    return (
      <Portal>
        <div
          ref={containerRef}
          className={styles.container}
          style={{ top: position.top, left: position.left }}
          onMouseDown={onMouseDown}
        >
          <div className={styles.noResults}>No matching suggestions</div>
        </div>
      </Portal>
    );
  }

  const activeOptionId = selectedSuggestion
    ? optionId(selectedSuggestion)
    : undefined;

  return (
    <Portal>
      <div
        ref={containerRef}
        className={styles.container}
        style={{ top: position.top, left: position.left }}
        onMouseDown={onMouseDown}
      >
        <div className={styles.panes}>
          {/* Left panel - Method list */}
          <div
            className={styles.listPanel}
            role="listbox"
            id={listboxId}
            aria-label={headerText ?? 'Expression suggestions'}
            aria-activedescendant={activeOptionId}
          >
            {groupedSuggestions.sortedCategories.map((category) => {
              const categorySuggestions = groupedSuggestions.groups[category] || [];

              return (
                <div key={category} role="group" aria-label={category}>
                  <div className={styles.categoryHeader}>{category}</div>
                  {categorySuggestions.map((suggestion) => {
                    const flatIndex = getFlatIndex(suggestion);
                    const isSelected = flatIndex === selectedIndex;

                    return (
                      <div
                        key={`${category}-${suggestion.label}`}
                        id={optionId(suggestion)}
                        ref={isSelected ? selectedRef : null}
                        className={mergeClasses(
                          styles.methodItem,
                          isSelected && styles.methodItemSelected
                        )}
                        role="option"
                        aria-selected={isSelected}
                        onClick={(e) => handleItemClick(suggestion, e)}
                        onMouseEnter={() => onSelectedIndexChange(flatIndex)}
                      >
                        {getTypeIcon(suggestion.returnType || suggestion.detail, styles)}
                        <span className={styles.methodName}>{suggestion.label}</span>
                        <span className={styles.methodSignature}>
                          {formatSignature(suggestion)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Right panel - Documentation */}
          <div className={styles.docPanel}>
            {selectedSuggestion ? (
              <div className={styles.docContent}>
                {/* Header */}
                <div className={styles.docHeader}>
                  <span className={styles.docTitle}>{selectedSuggestion.label}</span>
                  <span className={styles.docSignature}>
                    {formatSignature(selectedSuggestion)}
                  </span>
                  <div className={styles.docReturnType}>
                    <ArrowRightFilled fontSize={12} />
                    <span className={styles.docReturnTypeBadge}>
                      {selectedSuggestion.returnType || selectedSuggestion.detail}
                    </span>
                  </div>
                </div>

                <Divider />

                {/* Description */}
                {selectedSuggestion.documentation && (
                  <div className={styles.docSection}>
                    <div className={styles.docSectionTitle}>Description</div>
                    <Text className={styles.docDescription}>
                      {selectedSuggestion.documentation}
                    </Text>
                  </div>
                )}

                {/* Parameters */}
                {selectedSuggestion.args && selectedSuggestion.args.length > 0 && (
                  <div className={styles.docSection}>
                    <div className={styles.docSectionTitle}>Parameters</div>
                    <table className={styles.paramsTable}>
                      <tbody>
                        {selectedSuggestion.args.map((arg, idx) => (
                          <tr key={idx} className={styles.paramsRow}>
                            <td className={styles.paramsCell}>
                              <span className={styles.paramName}>{arg.name}</span>
                              {arg.optional && (
                                <span className={styles.paramOptional}> (optional)</span>
                              )}
                            </td>
                            <td className={styles.paramsCell}>
                              <span className={styles.paramType}>{arg.type}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Example */}
                {selectedSuggestion.example && (
                  <div className={styles.docSection}>
                    <div className={styles.docSectionTitle}>Example</div>
                    <div className={styles.exampleBlock}>{selectedSuggestion.example}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.emptyDoc}>Select a method to view documentation</div>
            )}
          </div>
        </div>

        {/* Keyboard-hint footer — same pattern as the rail's function
            picker so the two surfaces feel like cousins. aria-hidden
            because screen-reader users get the same affordances via
            aria-activedescendant + standard listbox semantics. */}
        <div className={styles.footer} aria-hidden="true">
          <span className={styles.hint}>
            <span className={styles.kbd}>↑↓</span> navigate
          </span>
          <span className={styles.hint}>
            <span className={styles.kbd}>↵</span> insert
          </span>
          <span className={styles.hint}>
            <span className={styles.kbd}>tab</span> insert
          </span>
          <span className={styles.hint}>
            <span className={styles.kbd}>esc</span> close
          </span>
        </div>
      </div>
    </Portal>
  );
};

// =============================================================================
// HELPER FUNCTIONS FOR GENERATING SUGGESTIONS
// =============================================================================

/**
 * Convert an ExtensionMethodMeta to an AutocompleteSuggestion
 */
export function methodToSuggestion(
  method: ExtensionMethodMeta,
  category?: string
): AutocompleteSuggestion {
  const resolvedCategory = method.category || category;
  const suggestion: AutocompleteSuggestion = {
    label: method.name,
    type: method.isProperty ? 'property' : 'method',
    detail: method.returnType,
    returnType: method.returnType,
    documentation: method.description,
    insertText: method.isProperty ? method.name : `${method.name}()`,
    ...(resolvedCategory && { category: resolvedCategory }),
  };
  if (method.example) {
    suggestion.example = method.example;
  }
  if (method.args) {
    suggestion.args = method.args.map((arg) => ({
      name: arg.name,
      type: arg.type,
      ...(arg.optional !== undefined && { optional: arg.optional }),
      ...(arg.description && { description: arg.description }),
    }));
  }
  return suggestion;
}

/**
 * Create a variable suggestion
 */
export function createVariableSuggestion(
  name: string,
  description: string,
  returnType: string,
  example?: string
): AutocompleteSuggestion {
  const suggestion: AutocompleteSuggestion = {
    label: name,
    type: 'variable',
    detail: returnType,
    returnType,
    documentation: description,
    insertText: name,
    category: 'Variables',
  };
  if (example) {
    suggestion.example = example;
  }
  return suggestion;
}

/**
 * Create a function suggestion
 */
export function createFunctionSuggestion(
  name: string,
  description: string,
  returnType: string,
  args?: Array<{ name: string; type: string; optional?: boolean }>,
  example?: string
): AutocompleteSuggestion {
  const suggestion: AutocompleteSuggestion = {
    label: name,
    type: 'function',
    detail: returnType,
    returnType,
    documentation: description,
    insertText: `${name}()`,
    category: 'Functions',
  };
  if (args) {
    suggestion.args = args;
  }
  if (example) {
    suggestion.example = example;
  }
  return suggestion;
}

ExpressionAutocomplete.displayName = 'ExpressionAutocomplete';
